import React, { useState } from 'react';
import styles from './InsuranceExportedBatchesListItem.module.scss';
import {
	InsuranceBatch,
	InsuranceReportBatch,
	InsuranceReportType,
	useCreateInsuranceReportExportFilesMutation,
} from '../../../../graphql/graphql';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import InsuranceExportedBatchesItemsListModal from './InsuranceExportedBatchesItemsListModal';
import useNotification from '../../../../Hooks/useNotification.hook';
import { joinClasses } from '../../../../Utils/className.util';
import Text from '../../../../Components/Base/Text/Text';

interface Props {
	item: InsuranceBatch;
	onReload: () => void;
	rootWorkplaceId: string;
}

const InsuranceExportedBatchesListItem: React.FC<Props> = ({
	item,
	rootWorkplaceId,
	onReload,
}) => {
	const [isItemListOpen, setIsItemListOpen] = useState(false);
	const [createInsuranceReportExport] =
		useCreateInsuranceReportExportFilesMutation();
	const { showNotificationSuccess } = useNotification();

	const handleExport = async () => {
		const result = await createInsuranceReportExport({
			input: {
				rootWorkplaceId,
				insuranceCompanyCode: item.insuranceCompanyCode!,
				month: item.periodMonth,
				year: item.periodYear,
				type: InsuranceReportType.Correction,
			},
		});

		if ('data' in result) {
			showNotificationSuccess({
				content: 'component.insuranceCompanyInsuranceExportGen.successMessage',
			});
		}
	};

	return (
		<>
			<div
				className={joinClasses([
					styles.wrapper,
					item.needCorrection ? styles.invalid : '',
				])}>
				<div className={styles.date}>
					<div>
						<FormattedMomentDate
							date={item.createdAt}
							format={DateFormat.DATE_TIME}
						/>
					</div>
				</div>
				<div className={styles.content}>
					<div className={styles.items}>
						<div className={styles.item}>
							<Text
								tx='component.insuranceExportedBatchesListItem.insuranceCompany'
								className={styles.label}
								colon
							/>
							<div>{item.insuranceCompanyTerm?.name}</div>
						</div>
						<div className={styles.item}>
							<Text
								tx='component.insuranceExportedBatchesListItem.period'
								className={styles.label}
								colon
							/>
							<div>
								{item.periodMonth}/{item.periodYear}
							</div>
						</div>
						{item.doctor && (
							<div className={styles.item}>
								<Text
									tx='component.insuranceExportedBatchesListItem.doctor'
									className={styles.label}
									colon
								/>
								<div>{item.doctor.profile?.fullName}</div>
							</div>
						)}
						{item.workplace && (
							<div className={styles.item}>
								<Text
									tx='component.insuranceExportedBatchesListItem.workplace'
									className={styles.label}
									colon
								/>
								<div>{item.workplace.name}</div>
							</div>
						)}
					</div>
					{item.logs && <div className={styles.logs}>{item.logs}</div>}
				</div>
				<div className={styles.controls}>
					{item.needCorrection && (
						<LinkButton
							theme={Theme.BLUE}
							title='component.insuranceExportedBatchesListItem.exportCorrectionFile'
							onClick={handleExport}
						/>
					)}
					<LinkButton
						title='component.insuranceExportedBatchesListItem.detail'
						theme={Theme.BLUE}
						onClick={() => setIsItemListOpen(true)}
					/>
				</div>
			</div>
			{isItemListOpen && (
				<InsuranceExportedBatchesItemsListModal
					isOpen
					items={item.items as InsuranceReportBatch[]}
					onClose={() => setIsItemListOpen(false)}
					onReload={onReload}
				/>
			)}
		</>
	);
};

export default InsuranceExportedBatchesListItem;
